import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7b338k9quqsda8uerag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-sans">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-40 pb-16 bg-navy-900">
          <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4">Contact</p>
            <h1 className="font-heading font-bold text-white text-4xl md:text-5xl leading-tight mb-4">
              Contactez-nous
            </h1>
            <p className="text-white/60 text-lg">Pour toute demande de projet ou renseignement</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Info */}
              <div>
                <h2 className="font-heading font-bold text-navy-900 text-2xl mb-8">Informations de contact</h2>
                <div className="space-y-6 mb-10">
                  {[
                    { icon: 'ri-building-line', label: 'Entreprise', value: 'CICHORZ\nMariusz Slawomir Cichorz\nSIRET : 102 737 178 00016' },
                    { icon: 'ri-map-pin-line', label: 'Adresse', value: '57 Rue Lecourbe\n75015 Paris, France' },
                    { icon: 'ri-phone-line', label: 'Téléphone', value: '+33 7 58 24 80 29', href: 'tel:+33758248029' },
                    { icon: 'ri-mail-line', label: 'Email', value: 'mariuszc1chorzz@outlook.com', href: 'mailto:mariuszc1chorzz@outlook.com' },
                  ].map((item) => (
                    <div key={item.label} className="flex gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-navy-900/5 rounded-lg flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-xl text-navy-900`}></i>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-gray-400 font-medium mb-1">{item.label}</p>
                        {item.href ? (
                          <a href={item.href} className="text-navy-900 font-medium text-sm hover:underline cursor-pointer">{item.value}</a>
                        ) : (
                          <p className="text-navy-900 font-medium text-sm whitespace-pre-line">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Map */}
                <div className="rounded-xl overflow-hidden border border-gray-100 h-56">
                  <iframe
                    title="CICHORZ – 57 Rue Lecourbe, Paris"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2625.9!2d2.3008!3d48.8417!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e6703f3e3e3e3e%3A0x0!2s57+Rue+Lecourbe%2C+75015+Paris!5e0!3m2!1sfr!2sfr!4v1"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              </div>

              {/* Form */}
              <div>
                <div className="bg-gray-50 rounded-2xl p-8 md:p-10">
                  <h2 className="font-heading font-bold text-navy-900 text-2xl mb-8">Votre demande</h2>
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                        <i className="ri-check-line text-3xl text-green-600"></i>
                      </div>
                      <h3 className="font-heading font-semibold text-navy-900 text-xl mb-2">Message envoyé !</h3>
                      <p className="text-gray-500 text-sm">Nous vous répondrons dans les plus brefs délais.</p>
                    </div>
                  ) : (
                    <form
                      data-readdy-form
                      id="contact-cichorz"
                      onSubmit={handleSubmit}
                      className="space-y-5"
                    >
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" htmlFor="nom">Nom *</label>
                        <input
                          id="nom"
                          name="nom"
                          type="text"
                          required
                          placeholder="Votre nom complet"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-navy-900 bg-white focus:outline-none focus:border-navy-900 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" htmlFor="email">Email *</label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          placeholder="votre@email.com"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-navy-900 bg-white focus:outline-none focus:border-navy-900 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" htmlFor="telephone">Téléphone</label>
                        <input
                          id="telephone"
                          name="telephone"
                          type="tel"
                          placeholder="+33 6 00 00 00 00"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-navy-900 bg-white focus:outline-none focus:border-navy-900 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" htmlFor="description">Description du projet *</label>
                        <textarea
                          id="description"
                          name="description"
                          required
                          rows={5}
                          maxLength={500}
                          placeholder="Décrivez votre projet en quelques lignes..."
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-navy-900 bg-white focus:outline-none focus:border-navy-900 transition-colors resize-none"
                        ></textarea>
                        <p className="text-xs text-gray-400 mt-1">Maximum 500 caractères</p>
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-navy-900 text-white font-semibold py-3.5 rounded-lg hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                      >
                        {loading ? 'Envoi en cours...' : 'Envoyer'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
